import React, { useState } from 'react';
import { X, PenTool as ToolIcon, Edit2 } from 'lucide-react';
import { Staff, Tool, ExperienceLevel, EmploymentStatus } from '../types';
import { supabase } from '../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface StaffModalProps {
  staff: Staff;
  tools: Tool[];
  onClose: () => void;
  onUpdate: (updatedStaff: Staff) => void;
  isNew?: boolean;
  viewOnly?: boolean;
}

export function StaffModal({ staff, tools, onClose, onUpdate, isNew = false, viewOnly = false }: StaffModalProps) {
  const [editedStaff, setEditedStaff] = useState(staff);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(isNew);

  const experienceLevels: ExperienceLevel[] = ['junior', 'mid-level', 'senior', 'lead'];
  const employmentStatuses: { value: EmploymentStatus; label: string }[] = [
    { value: 'w2', label: 'W2' },
    { value: '1099_full_time', label: '1099 Full Time' },
    { value: '1099_part_time', label: '1099 Part Time' }
  ];
  const years = Array.from({ length: 9 }, (_, i) => (2017 + i).toString());

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (isNew) {
        const { data: newStaff, error: createError } = await supabase
          .from('staff')
          .insert({
            name: editedStaff.name,
            role: editedStaff.role,
            bio: editedStaff.bio,
            experience_level: editedStaff.experience_level,
            employment_status: editedStaff.employment_status,
            year_joined: editedStaff.year_joined
          })
          .select()
          .single();

        if (createError) throw createError;
        if (!newStaff) throw new Error('Failed to create staff member');

        if (editedStaff.tools.length > 0) {
          const staffTools = editedStaff.tools.map(tool => ({
            staff_id: newStaff.id,
            tool_id: tool.id
          }));

          const { error: toolError } = await supabase
            .from('staff_tools')
            .insert(staffTools);

          if (toolError) throw toolError;
        }

        const { data: finalStaff, error: finalError } = await supabase
          .from('staff')
          .select(`
            *,
            tools:staff_tools (
              tool:tools (
                id,
                name
              )
            )
          `)
          .eq('id', newStaff.id)
          .single();

        if (finalError) throw finalError;
        if (!finalStaff) throw new Error('Failed to fetch created staff member');

        const transformedStaff: Staff = {
          id: finalStaff.id,
          name: finalStaff.name,
          role: finalStaff.role,
          bio: finalStaff.bio,
          experience_level: finalStaff.experience_level,
          employment_status: finalStaff.employment_status,
          year_joined: finalStaff.year_joined,
          tools: finalStaff.tools.map((t: any) => t.tool),
          created_at: finalStaff.created_at
        };

        onUpdate(transformedStaff);
        setIsEditing(false);
      } else {
        const { data: existingStaff, error: checkError } = await supabase
          .from('staff')
          .select('id')
          .eq('id', staff.id)
          .maybeSingle();

        if (checkError) throw checkError;
        if (!existingStaff) throw new Error('Staff member not found');

        const { data: updatedStaff, error: updateError } = await supabase
          .from('staff')
          .update({
            name: editedStaff.name,
            role: editedStaff.role,
            bio: editedStaff.bio,
            experience_level: editedStaff.experience_level,
            employment_status: editedStaff.employment_status,
            year_joined: editedStaff.year_joined
          })
          .eq('id', staff.id)
          .select()
          .single();

        if (updateError) throw updateError;
        if (!updatedStaff) throw new Error('Failed to update staff member');

        const { data: currentTools } = await supabase
          .from('staff_tools')
          .select('tool_id')
          .eq('staff_id', staff.id);

        const currentToolIds = new Set(currentTools?.map(t => t.tool_id) || []);
        const newToolIds = new Set(editedStaff.tools.map(t => t.id));

        const toolsToRemove = Array.from(currentToolIds)
          .filter(toolId => !newToolIds.has(toolId));

        if (toolsToRemove.length > 0) {
          await supabase
            .from('staff_tools')
            .delete()
            .eq('staff_id', staff.id)
            .in('tool_id', toolsToRemove);
        }

        const toolsToAdd = editedStaff.tools
          .filter(tool => !currentToolIds.has(tool.id))
          .map(tool => ({
            staff_id: staff.id,
            tool_id: tool.id
          }));

        if (toolsToAdd.length > 0) {
          await supabase
            .from('staff_tools')
            .insert(toolsToAdd);
        }

        const { data: finalStaff, error: finalError } = await supabase
          .from('staff')
          .select(`
            *,
            tools:staff_tools (
              tool:tools (
                id,
                name
              )
            )
          `)
          .eq('id', staff.id)
          .single();

        if (finalError) throw finalError;
        if (!finalStaff) throw new Error('Failed to fetch updated staff member');

        const transformedStaff: Staff = {
          id: finalStaff.id,
          name: finalStaff.name,
          role: finalStaff.role,
          bio: finalStaff.bio,
          experience_level: finalStaff.experience_level,
          employment_status: finalStaff.employment_status,
          year_joined: finalStaff.year_joined,
          tools: finalStaff.tools.map((t: any) => t.tool),
          created_at: finalStaff.created_at
        };

        onUpdate(transformedStaff);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving staff member:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const toggleTool = (tool: Tool) => {
    const hasTool = editedStaff.tools.some(t => t.id === tool.id);
    if (hasTool) {
      setEditedStaff({
        ...editedStaff,
        tools: editedStaff.tools.filter(t => t.id !== tool.id)
      });
    } else {
      setEditedStaff({
        ...editedStaff,
        tools: [...editedStaff.tools, tool]
      });
    }
  };

  const getExperienceLevelColor = (level: string) => {
    switch (level) {
      case 'junior':
        return 'bg-green-100 text-green-800';
      case 'mid-level':
        return 'bg-blue-100 text-blue-800';
      case 'senior':
        return 'bg-purple-100 text-purple-800';
      case 'lead':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEmploymentStatusLabel = (status: string) => {
    switch (status) {
      case 'w2':
        return 'W2';
      case '1099_full_time':
        return '1099 Full Time';
      case '1099_part_time':
        return '1099 Part Time';
      default:
        return status;
    }
  };

  const getEmploymentStatusColor = (status: string) => {
    switch (status) {
      case 'w2':
        return 'bg-emerald-100 text-emerald-800';
      case '1099_full_time':
        return 'bg-amber-100 text-amber-800';
      case '1099_part_time':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (viewOnly && !isEditing) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-2xl h-[90vh] flex flex-col">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-gray-900">{staff.name}</h2>
              <div className="flex items-center space-x-2">
                {!viewOnly && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
              <span>{staff.role}</span>
              <span>•</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExperienceLevelColor(staff.experience_level)}`}>
                {staff.experience_level.charAt(0).toUpperCase() + staff.experience_level.slice(1)}
              </span>
              <span>•</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEmploymentStatusColor(staff.employment_status)}`}>
                {getEmploymentStatusLabel(staff.employment_status)}
              </span>
              <span>•</span>
              <span>Joined {staff.year_joined}</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              <div className="prose prose-sm max-w-none text-gray-600">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {staff.bio}
                </ReactMarkdown>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Tools & Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  {staff.tools.map((tool) => (
                    <span
                      key={tool.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      <ToolIcon className="w-3 h-3 mr-1" />
                      {tool.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {isNew ? 'Add Team Member' : 'Edit Team Member'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={editedStaff.name}
                onChange={(e) => setEditedStaff({ ...editedStaff, name: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter team member's name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <input
                type="text"
                value={editedStaff.role}
                onChange={(e) => setEditedStaff({ ...editedStaff, role: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter role (e.g., Developer, Designer)"
              />
            </div>

            <div>
              <textarea
                value={editedStaff.bio}
                onChange={(e) => setEditedStaff({ ...editedStaff, bio: e.target.value })}
                rows={4}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter team member's bio (Markdown supported)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Experience Level
              </label>
              <select
                value={editedStaff.experience_level}
                onChange={(e) => setEditedStaff({
                  ...editedStaff,
                  experience_level: e.target.value as ExperienceLevel
                })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                {experienceLevels.map(level => (
                  <option key={level} value={level}>
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Status
              </label>
              <select
                value={editedStaff.employment_status}
                onChange={(e) => setEditedStaff({
                  ...editedStaff,
                  employment_status: e.target.value as EmploymentStatus
                })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                {employmentStatuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year Joined
              </label>
              <select
                value={editedStaff.year_joined}
                onChange={(e) => setEditedStaff({
                  ...editedStaff,
                  year_joined: e.target.value
                })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tools & Technologies
              </label>
              <div className="flex flex-wrap gap-2">
                {tools.map((tool) => {
                  const isSelected = editedStaff.tools.some(t => t.id === tool.id);
                  return (
                    <button
                      key={tool.id}
                      onClick={() => toggleTool(tool)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                        isSelected
                          ? 'bg-indigo-100 text-indigo-800'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <ToolIcon className="w-4 h-4 mr-1" />
                      {tool.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !editedStaff.name.trim() || !editedStaff.role.trim()}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : isNew ? 'Create Team Member' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}